import Cookies, { CookieSetOptions } from 'universal-cookie';
import { UserDTO } from '../api/model';
import CognitoUtil, { CognitoAuthResultType } from './cognito-util';

export default class UserHelper {
    private static cookies = new Cookies();
    private static COOKIE_IDENTIFIER = 'soar-id-token';
    private static COOKIE_ACCESS = 'soar-access-token';
    private static COOKIE_REFRESH = 'soar-refresh-token';
    private static COOKIE_OPTIONS: CookieSetOptions = {
        path: '/',
        secure: true,
        sameSite: 'strict',
        maxAge: 31536000, // 1 year
    };

    public static saveToken(authResults: CognitoAuthResultType) {
        const COOKIE_ID_TOKEN = `${this.COOKIE_IDENTIFIER}`;
        const COOKIE_ACCESS_TOKEN = `${this.COOKIE_ACCESS}`;
        const COOKIE_REFRESH_TOKEN = `${this.COOKIE_REFRESH}`;

        this.cookies.set(COOKIE_ID_TOKEN, authResults.IdToken, {
            ...this.COOKIE_OPTIONS,
            maxAge: authResults.ExpiresIn,
        });
        this.cookies.set(COOKIE_ACCESS_TOKEN, authResults.AccessToken, {
            ...this.COOKIE_OPTIONS,
            maxAge: authResults.ExpiresIn,
        });

        if (authResults.RefreshToken) {
            this.cookies.set(COOKIE_REFRESH_TOKEN, authResults.RefreshToken, this.COOKIE_OPTIONS);
        }
    }

    public static getIdToken(): string | undefined {
        const COOKIE_ID_TOKEN = `${this.COOKIE_IDENTIFIER}`;
        return this.cookies.get(COOKIE_ID_TOKEN) || undefined;
    }

    public static getAccessToken(): string | undefined {
        const COOKIE_ACCESS_TOKEN = `${this.COOKIE_ACCESS}`;
        return this.cookies.get(COOKIE_ACCESS_TOKEN) || undefined;
    }

    public static getRefreshToken(): string | undefined {
        const COOKIE_REFRESH_TOKEN = `${this.COOKIE_REFRESH}`;
        return this.cookies.get(COOKIE_REFRESH_TOKEN) || undefined;
    }

    public static async refreshAuth(): Promise<string | undefined> {
        const COOKIE_REFRESH_TOKEN = `${this.COOKIE_REFRESH}`;
        const refreshToken = this.cookies.get(COOKIE_REFRESH_TOKEN);

        if (refreshToken) {
            try {
                const refreshAuthResponse = await CognitoUtil.refreshToken(refreshToken);

                this.saveToken(refreshAuthResponse);

                return refreshAuthResponse.IdToken as string;
            } catch (err) {
                console.log('Refreshing token error');
                this.resetTokens();
            }
        }
        return undefined;
    }

    public static getDisplayUserName(user: UserDTO): string {
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }

        if (user.firstName && !user.lastName) {
            return `${user.firstName}`;
        }

        if (!user.firstName && user.name) {
            return user.name;
        }

        return 'Soar User';
    }

    public static resetTokens() {
        const COOKIE_ID_TOKEN = `${this.COOKIE_IDENTIFIER}`;
        const COOKIE_ACCESS_TOKEN = `${this.COOKIE_ACCESS}`;
        const COOKIE_REFRESH_TOKEN = `${this.COOKIE_REFRESH}`;
        const options = {
            path: '/',
        };
        this.cookies.remove(COOKIE_ID_TOKEN, options);
        this.cookies.remove(COOKIE_ACCESS_TOKEN, options);
        this.cookies.remove(COOKIE_REFRESH_TOKEN, options);
    }

    public static isPasswordValid(password: string): boolean {
        return password.length >= 6;
    }

    public static setCustomCookie = (cookieName: string, cookieValue: string, maxAge?: number): void => {
        this.cookies.set(cookieName, cookieValue, { ...this.COOKIE_OPTIONS, maxAge: maxAge ?? 2629800 });
    };

    public static removeCustomCookie = (cookieName: string): void => {
        this.cookies.remove(cookieName);
    };

    public static getCustomCookie = (cookieName: string): string => {
        return this.cookies.get(cookieName);
    };

    public static formatToUppercaseAndLowercaseText = (layerName: string) => {
        return layerName
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    public static isEmailValid(email: string): boolean {
        return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,8}$/i.test(email);
    }
}
